/**
 * Claude AI Service - Frontend Proxy
 *
 * SECURITY NOTE:
 * This service now calls our secure Firebase Cloud Function instead of
 * directly calling Claude API. This keeps the API key secure on the backend
 * and enables proper authentication, authorization, and audit logging.
 */

import { getFunctions, httpsCallable } from 'firebase/functions';
import { getAuth } from 'firebase/auth';
import { AIEnhanced } from '../models/SessionNote';

interface EnhanceSessionNotesRequest {
  sessionNoteId: string;
}

interface EnhanceSessionNotesResponse {
  success: boolean;
  data?: {
    professionalSummary: string;
    programNotes: string;
    behaviorAnalysis: string;
    clinicalObservations: string;
    suggestions: string[];
  };
  error?: string;
}

class ClaudeAIService {
  /**
   * Enhance session notes via secure backend function
   */
  async enhanceSessionNotes(sessionNoteId: string): Promise<Omit<AIEnhanced, 'generatedAt'>> {
    try {
      // Get Firebase Functions instance
      const functions = getFunctions();

      // Get the callable function
      const enhanceSessionNotes = httpsCallable<
        EnhanceSessionNotesRequest,
        EnhanceSessionNotesResponse
      >(functions, 'enhanceSessionNotes');

      // Ensure user is authenticated
      const auth = getAuth();
      const user = auth.currentUser;

      if (!user) {
        throw new Error('User must be authenticated to enhance session notes');
      }

      console.log('Calling secure backend to enhance session notes...');

      // Call the backend function
      // Firebase automatically includes the auth token in the request
      const result = await enhanceSessionNotes({ sessionNoteId });

      if (!result.data.success || !result.data.data) {
        throw new Error(result.data.error || 'Failed to enhance session notes');
      }

      console.log('AI enhancement completed successfully');

      // Return the enhanced data with model info.
      // Backend was migrated from claude-3-sonnet-20240229 (retired 2025-07-21)
      // to claude-sonnet-4-6 on 2026-04-28; keep this string in sync with
      // functions/src/services/claudeAPI.ts MODEL.
      return {
        ...result.data.data,
        modelUsed: 'claude-sonnet-4-6',
      };
    } catch (error: any) {
      console.error('Error calling backend function:', error);

      // Provide user-friendly error messages
      if (error.code === 'unauthenticated') {
        throw new Error('You must be logged in to use AI enhancement');
      }

      if (error.code === 'permission-denied') {
        throw new Error('You do not have permission to enhance this session note');
      }

      if (error.code === 'not-found') {
        throw new Error('Session note not found');
      }

      if (error.code === 'failed-precondition') {
        throw new Error('Session note does not have an RBT draft to enhance');
      }

      // Generic error
      throw new Error(
        error.message || 'Failed to enhance notes with AI. Please try again later.'
      );
    }
  }

  /**
   * Check if AI enhancement is available
   * Always returns true since backend handles configuration
   */
  isConfigured(): boolean {
    return true;
  }
}

export const claudeAIService = new ClaudeAIService();
